import { Component, Input, Output, EventEmitter, forwardRef, OnInit, ViewEncapsulation, inject, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { TranslateService } from '@ngx-translate/core';
import type { ClassicEditor, EditorConfig } from 'ckeditor5';
import { getCKEditorPluginConfig } from './ckeditor-plugin-config.helper';

@Component({
    standalone: true,
    selector: 'app-rich-text-editor',
    imports: [FormsModule, CKEditorModule],
    templateUrl: './rich-text-editor.component.html',
    styleUrls: ['./rich-text-editor.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RichTextEditorComponent),
            multi: true
        }]
})
export class RichTextEditorComponent implements ControlValueAccessor, OnInit {
  @Input() placeholder = 'Enter your message...';
  @Input() minHeight = '200px';
  @Input() maxHeight = '400px';
  @Input() spellcheck = true;
  @Output() contentChange = new EventEmitter<string>();

  private translateService = inject(TranslateService);

  public Editor: typeof ClassicEditor | null = null;
  public editorConfig: EditorConfig | null = null;
  public value = '';

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  private t = (key: string) => this.translateService.instant(key);

  private getLanguage(): string {
    const lang = this.translateService.currentLang;
    const langMap: Record<string, string> = {
      'es': 'es', 'es-CL': 'es', 'es-ES': 'es',
      'fr': 'fr', 'fr-FR': 'fr',
      'de': 'de', 'de-DE': 'de',
      'pt': 'pt', 'pt-BR': 'pt'
    };
    return langMap[lang] || 'en';
  }

  private colorConfig = (colors: [string, string][]) =>
    colors.map(([color, key]) => ({ color, label: this.t(key) }));

  async ngOnInit(): Promise<void> {
    const ck = await import(/* webpackChunkName: "ckeditor5" */ 'ckeditor5');

    const baseColors: [string, string][] = [
      ['#000000', 'Black'], ['#4d4d4d', 'Dark Grey'], ['#999999', 'Grey'],
      ['#e6e6e6', 'Light Grey'], ['#ffffff', 'White']
    ];

    const fontColors: [string, string][] = [
      ...baseColors,
      ['#e64c4c', 'Red'], ['#e6994c', 'Orange'], ['#e6e64c', 'Yellow'],
      ['#4ce64c', 'Green'], ['#4c4ce6', 'Blue'], ['#994ce6', 'Purple'], ['#25245c', 'Primary']
    ];

    const bgColors: [string, string][] = [
      ...baseColors,
      ['#ffcccc', 'Light Red'], ['#ffe6cc', 'Light Orange'], ['#ffffcc', 'Light Yellow'],
      ['#ccffcc', 'Light Green'], ['#ccccff', 'Light Blue'], ['#e6ccff', 'Light Purple']
    ];

    const pluginConfig = getCKEditorPluginConfig(ck, false);

    this.editorConfig = {
      licenseKey: pluginConfig.licenseKey ?? 'GPL',
      language: this.getLanguage(),
      plugins: pluginConfig.plugins,
      toolbar: {
        items: pluginConfig.toolbar,
        shouldNotGroupWhenFull: false
      },
      heading: {
        options: [
          { model: 'paragraph', title: this.t('Paragraph'), class: 'ck-heading_paragraph' },
          ...([1, 2, 3, 4] as const).map(n => ({
            model: `heading${n}` as const,
            view: `h${n}` as const,
            title: this.t(`Heading ${n}`),
            class: `ck-heading_heading${n}`
          }))
        ]
      },
      fontSize: {
        options: [
          { title: this.t('Tiny'), model: '10px' },
          { title: this.t('Small'), model: '12px' },
          { title: this.t('Default'), model: '14px' },
          { title: this.t('Big'), model: '18px' },
          { title: this.t('Huge'), model: '24px' }
        ]
      },
      fontFamily: {
        options: [
          'default', 'Arial, Helvetica, sans-serif', 'Courier New, Courier, monospace',
          'Georgia, serif', 'Times New Roman, Times, serif', 'Verdana, Geneva, sans-serif'
        ],
        supportAllValues: true
      },
      fontColor: { colors: this.colorConfig(fontColors) },
      fontBackgroundColor: { colors: this.colorConfig(bgColors) },
      highlight: {
        options: [
          { model: 'yellowMarker', class: 'marker-yellow', title: this.t('Yellow marker'), color: '#fdfd77', type: 'marker' as const },
          { model: 'greenMarker', class: 'marker-green', title: this.t('Green marker'), color: '#63f963', type: 'marker' as const },
          { model: 'pinkMarker', class: 'marker-pink', title: this.t('Pink marker'), color: '#fc7999', type: 'marker' as const },
          { model: 'blueMarker', class: 'marker-blue', title: this.t('Blue marker'), color: '#72cdfd', type: 'marker' as const }
        ]
      },
      table: {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties', 'toggleTableCaption', 'tableColumnResize'],
        tableProperties: { alignment: { useInlineStyles: true } }
      },
      htmlSupport: {
        allow: [
          { name: /.*/, attributes: true, classes: true, styles: true }
        ]
      },
      link: { addTargetToExternalLinks: true, defaultProtocol: 'https://' },
      placeholder: this.placeholder
    };

    this.Editor = ck.ClassicEditor;
  }

  writeValue(value: string): void { this.value = value || ''; }
  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }

  onModelChange(data: string): void {
    this.onChange(data);
    this.contentChange.emit(data);
  }

  onFocus(): void { this.onTouched(); }

  onReady(editor: ClassicEditor): void {
    const el = editor?.ui?.view?.editable?.element;
    if (!el) return;
    Object.assign(el.style, { minHeight: this.minHeight, maxHeight: this.maxHeight });
    el.spellcheck = this.spellcheck;
    el.lang = this.getLanguage();
  }
}
